import { DataLayerContext } from 'features/dataLayers/DataLayerProvider'
import { GeckoBinnedDataBuffer } from 'features/dataLayers/geckoBinnedDataBuffer'
import { DeleteCacheValue } from 'features/rolodex'
import { MemoizedFunction } from 'lodash'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { GeckoComponentRect } from 'types'
import { cdfDataFileName } from 'utils/constants'

type CdfId = string
type FilePath = string
type GenerationNumber = number
type CropSelection = string

interface CdfBinnedDataStorageFns {
  getCachedBinnedDataBufferAccessor: (
    id: CdfId,
    filePath?: FilePath,
    cropSelection?: GeckoComponentRect
  ) =>
    | {
        retrieveBinnedDataBuffer: () => Promise<GeckoBinnedDataBuffer>
        generation: GenerationNumber | undefined
      }
    | undefined

  putBinnedDataBufferInCache: (props: {
    id: CdfId
    filePath?: FilePath
    generation: GenerationNumber
    data: (() => Promise<GeckoBinnedDataBuffer>) & MemoizedFunction
    cropSelection?: GeckoComponentRect
  }) => void
  deleteCachedBinnedDataBuffer: (
    id: CdfId,
    filePath?: FilePath,
    cropSelection?: GeckoComponentRect
  ) => void
}

function gcrToID(gcr: GeckoComponentRect | undefined) {
  return gcr ? JSON.stringify(gcr.toJSON()) : 'NO_SELECTION'
}

/**
 * A hook that returns utility functions for storing and retrieving binned data
 */
export function useBinnedDataBufferCache(): CdfBinnedDataStorageFns {
  const { cdfDataFetchCache, setInCdfDataFetchCache } =
    useContext(DataLayerContext)

  // ----------------------------------------------------------------------------
  // Handle Temporary Data

  // a ledger of the data uploaded via this hook, so we can clear it on unmount if needed
  const uploadedDataLedger = useRef<
    Map<CdfId, Map<string, Map<FilePath, GenerationNumber>>>
  >(new Map())

  const upsertInLedger = useCallback(
    (props: {
      id: CdfId
      filePath: FilePath
      cropSelection: CropSelection
      generation: GenerationNumber
    }) => {
      const hasValue =
        uploadedDataLedger.current.has(props.id) &&
        uploadedDataLedger.current.get(props.id)!.has(props.cropSelection)
      if (!hasValue) {
        uploadedDataLedger.current.set(props.id, new Map())
        uploadedDataLedger.current
          .get(props.id)!
          .set(props.cropSelection, new Map())
      }
      uploadedDataLedger.current
        .get(props.id)!
        .get(props.cropSelection)!
        .set(props.filePath, props.generation)
    },
    []
  )

  const deleteInLedger = useCallback(
    (props: { id: CdfId; filePath: FilePath }) => {
      uploadedDataLedger.current.delete(props.id)
    },
    []
  )

  // a function to remove any data in the cdfDataFetchCache that was uploaded via this hook
  const clearTemporaryData = useCallback(() => {
    for (const [id, fileMap] of uploadedDataLedger.current) {
      for (const [cropSelection, selectionMap] of fileMap) {
        for (const [filePath, generation] of selectionMap) {
          const generationNumInCache = cdfDataFetchCache
            .get(id)
            ?.get(cropSelection)
            ?.get(filePath)?.generation
          // clear all temporary data out of the cache
          // we can tell if the data was from this hook by checking the generation number
          if (generationNumInCache === generation) {
            setInCdfDataFetchCache({
              id,
              filePath: filePath,
              cropSelection,
              value: DeleteCacheValue,
            })
          }
        }
      }
    }

    uploadedDataLedger.current.clear()
  }, [
    // should never re-initialize (ref variable)
    cdfDataFetchCache,
    // should never change (only depends on ref variable)
    setInCdfDataFetchCache,
  ])

  useEffect(() => {
    return clearTemporaryData
  }, [clearTemporaryData])

  // ----------------------------------------------------------------------------
  // Main Functions

  const getCachedBinnedDataBufferAccessor: CdfBinnedDataStorageFns['getCachedBinnedDataBufferAccessor'] =
    useCallback(
      (
        id: CdfId,
        filePath: FilePath = cdfDataFileName,
        cropSelection = undefined
      ) => {
        const specificIdCache = cdfDataFetchCache.get(id)
        const specificCropCache = specificIdCache?.get(gcrToID(cropSelection))
        if (specificCropCache) {
          const cachedValues = specificCropCache?.get(filePath)
          if (cachedValues) {
            return {
              retrieveBinnedDataBuffer: cachedValues.getData,
              generation: cachedValues.generation,
            }
          }
        }
        return undefined
      },
      [cdfDataFetchCache]
    )

  const putBinnedDataBufferInCache: CdfBinnedDataStorageFns['putBinnedDataBufferInCache'] =
    useCallback(
      (props: {
        id: CdfId
        filePath?: FilePath
        generation: GenerationNumber
        cropSelection?: GeckoComponentRect
        data: (() => Promise<GeckoBinnedDataBuffer>) & MemoizedFunction
      }) => {
        const {
          id,
          filePath = cdfDataFileName,
          generation,
          cropSelection,
          data,
        } = props

        setInCdfDataFetchCache({
          id,
          filePath,
          value: {
            generation,
            getData: data,
          },
          cropSelection: gcrToID(cropSelection),
        })
        upsertInLedger({
          id,
          filePath,
          cropSelection: gcrToID(cropSelection),
          generation,
        })
      },
      [setInCdfDataFetchCache, upsertInLedger]
    )

  const deleteCachedBinnedDataBuffer: CdfBinnedDataStorageFns['deleteCachedBinnedDataBuffer'] =
    useCallback(
      (
        id: CdfId,
        filePath: FilePath = cdfDataFileName,
        cropSelection = undefined
      ) => {
        setInCdfDataFetchCache({
          id,
          filePath: filePath,
          cropSelection: gcrToID(cropSelection),
          value: DeleteCacheValue,
        })
        deleteInLedger({ id, filePath: filePath })
      },
      [setInCdfDataFetchCache, deleteInLedger]
    )

  return {
    getCachedBinnedDataBufferAccessor,
    putBinnedDataBufferInCache,
    deleteCachedBinnedDataBuffer,
  }
}
